import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { TileSize } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { roofGroundDepth } from "./building_depth"

/**
 * Pairwise draw order for a body on its storey: standing, lying, climbing, in a jump or a fall. The world surface still
 * sorts by one zIndex each, but a body's is chosen from the pieces of the world it overlaps on screen: for each, whether
 * the body is drawn over or under it is read from the two's shapes, and the body is placed among their depths to agree
 * with as many as it can (weighted by how much of each it overlaps). A piece with no known shape keeps the order the
 * body's own depth gives it (entity_depth.ts).
 *
 * The shapes are the camera's: it looks down from the south, so of two things on screen the nearer stands further
 * south. What decides each pair (judged from pictures, draw_order/reviewed.txt):
 *   - an upright piece (a wall, a ladder) stands along a line on the ground, its foot line: a body whose soles are south
 *     of it is drawn over it, one level with it or north of it under it. A wall's foot line is its cell's south edge; a
 *     ladder's, its feet, near its cell's south edge.
 *   - a block's top lies a block's height up: what of the body is higher than that, beside it, is over it.
 *   - a hatch lies on the floor: under every body.
 *   - a roof's or an upper floor's tile covers its row, and its eave a little past it: a body is under a tile of its row
 *     or a row south of it, over one north of it -- a few px past the row's south edge, the roof's own depth
 *     (building_depth.ts): in a doorway, just out of it, the eave stays over the head. Each tile is a piece of its own:
 *     a floor drawn on a wall behind a body is over it, the same floor reaching over its head under it. A body drawn as
 *     high as the tile is on it, over it.
 *
 * Draw-order suite: client/src/__tests__/integration/draw_order.
 */
export interface Box { x: number; y: number; w: number; h: number }

export type DepthShape =
    /** Upright, its foot along ground row `y` (a wall's face or cap, a ladder). */
    | { kind: "upright"; y: number }
    /** The top of a block `height` px high over ground rows y0..y1. */
    | { kind: "top"; y0: number; y1: number; height: number }
    /** On the floor (a hatch). */
    | { kind: "floor" }
    /** A roof's or an upper floor's tile over row `row`, `height` px up. */
    | { kind: "cover"; row: number; height: number }
    | { kind: "unknown" }

export interface DepthPiece { z: number; box: Box; shape: DepthShape }

/** What a map renderer knows of the pieces on its surface: those whose art overlaps a world rect. */
export interface DepthPieceSource { depthPiecesAround(box: Box): DepthPiece[] }

const sources = new WeakMap<TileMapMetaProvider, DepthPieceSource>()

/** The renderer drawing `provider`'s storey offers its pieces to the bodies on it. */
export const registerDepthPieces = (provider: TileMapMetaProvider, source: DepthPieceSource): void => {
    sources.set(provider, source)
}

/** A ladder's art ends this far above its sprite's bottom edge: its feet. */
export const LADDER_FEET_INSET = 4

/**
 * How far a body reaches round its middle and up from its soles on screen, in every frame of each stance: the realist
 * body's frames share one 80px frame, its soles on row 70, so these are measured against that.
 */
export interface Reach { half: number; height: number }
export const WALKING: Reach = { half: 16, height: 60 }
/** Coming down from the storey above, the arms flung up and out. */
export const FALLING: Reach = { half: 21, height: 63 }
/** Fallen face down (the hurt animation's last frame): low and wide, from its soles. */
export const LYING: Reach = { half: 19, height: 38 }
export const BODY_HALF_WIDTH = WALKING.half
export const BODY_HEIGHT = WALKING.height

/**
 * Where a body is, for sorting it: the line on the ground it stands on (`ground`, its soles' row as the camera sees depth:
 * further south is nearer), how high it is drawn (`lift`: on a ladder's rungs, in a fall from the storey above), and what
 * it covers on screen (`box`).
 */
export interface BodyPlace { x: number; ground: number; box: Box; lift: number }

/**
 * A body upright on `ground`, its middle at `x`, drawn `lift` px above it, `stance` its frames' reach (`arc` px taller
 * still: a jump's arc).
 */
export const uprightPlace = (x: number, ground: number, lift = 0, arc = 0, stance: Reach = WALKING): BodyPlace =>
    ({ x, ground, lift, box: { x: x - stance.half, y: ground - lift - stance.height - arc, w: stance.half * 2, h: stance.height + arc } })

/** A body lying on `ground`, its middle at `x`. */
export const lyingPlace = (x: number, ground: number): BodyPlace => uprightPlace(x, ground, 0, 0, LYING)

/** The box of a body standing with its soles at (`x`, `soles`). */
export const bodyBox = (x: number, soles: number): Box => uprightPlace(x, soles).box

const overlapArea = (a: Box, b: Box): number =>
    Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y))

/**
 * Whether a body at `place` is drawn over or under `piece`; undefined: no shape to say. Every pixel of a body stands on its
 * ground line, however high: one drawn at screen row sy is (ground - sy) px up.
 */
export const relationTo = (place: BodyPlace, piece: DepthPiece): "over" | "under" | undefined => {
    const shape = piece.shape, ground = place.ground
    switch (shape.kind) {
        case "upright":
            return ground > shape.y ? "over" : "under"
        case "floor":
            return "over"
        case "top": {
            if (ground >= shape.y1) return "over"
            // Beside it: the body is over it where it stands higher than the top, under it below that.
            const from = Math.max(piece.box.y, place.box.y), to = Math.min(piece.box.y + piece.box.h, place.box.y + place.box.h)
            const higher = Math.max(0, Math.min(to, ground - shape.height) - from)
            return higher > (to - from) / 2 ? "over" : "under"
        }
        case "cover":
            // Drawn as high as it, or higher (a climber at a ladder's top, a body dropping from the storey above): on it.
            if (place.lift >= shape.height - 2) return "over"
            return 100 + ground - BODY_GROUND_OFFSET < roofGroundDepth(shape.row) ? "under" : "over"
        default:
            return undefined
    }
}

/** A piece with a shape counts this much more than one kept in the body's own order. */
const SHAPED_WEIGHT = 2
const NUDGE = 1e-4

/**
 * The depth for a body at `place`, sorted at `natural` by its own rule, among `pieces`: the depth agreeing with the most
 * of what it overlaps, the nearest to `natural` of those.
 */
export const placeAmong = (natural: number, place: BodyPlace, pieces: DepthPiece[]): number => {
    const wants: { z: number; over: boolean; weight: number }[] = []
    for (const piece of pieces) {
        const area = overlapArea(place.box, piece.box)
        if (!area) continue
        const shaped = relationTo(place, piece)
        if (shaped) wants.push({ z: piece.z, over: shaped === "over", weight: area * SHAPED_WEIGHT })
        else if (natural !== piece.z) wants.push({ z: piece.z, over: natural > piece.z, weight: area })
    }
    if (!wants.length) return natural
    const agreement = (depth: number) => wants.reduce((sum, want) => sum + ((depth > want.z) === want.over ? want.weight : 0), 0)
    let best = natural, bestScore = agreement(natural)
    for (const want of wants) for (const depth of [want.z - NUDGE, want.z + NUDGE]) {
        const score = agreement(depth)
        if (score > bestScore || score === bestScore && Math.abs(depth - natural) < Math.abs(best - natural)) {
            best = depth
            bestScore = score
        }
    }
    return best
}

/** `natural` placed among the pieces round a body at `place` on `provider`'s storey. */
export const pairwiseDepth = (provider: TileMapMetaProvider, place: BodyPlace, natural: number): number => {
    const source = sources.get(provider)
    return source ? placeAmong(natural, place, source.depthPiecesAround(place.box)) : natural
}
